import { Box, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { memo, useCallback, useContext, useMemo } from "react";
import { ReactComponent as Sentence } from "../../../assets/icons/sentence.svg";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import useRoute from "../../../hooks/useRoute";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard } from "../../../types/files";

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 400,
        height: 'fit-content',
        overflow: 'hidden',
        padding: 16,
        borderRadius: 12,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: 'all 300ms ease-in-out',
    },
    title: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Poppins',
        fontSize: '1.2rem',
        fontWeight: '600',
        color: '#666666',
    },
    label: {
        fontFamily: 'Poppins',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        "&:hover": {
            textDecoration: 'underline',
        }
    },
    container: {
        width: '100%',
        height: '100%',
        background: '#fff',
        border: `1px solid ${theme.colors.neutral['300']}`,
        boxShadow: '0px 4px 10px -1px rgba(16, 24, 40, 0.06)',
        borderRadius: 16,
        padding: 16,
        breakInside: 'avoid',
        transition: '0.05s ease-in-out',
    },
    noteBlock: {
        width: '100%',
        height: '100%',
        minHeight: '67px',
        maxHeight: '112px',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    noteText: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: theme.colors.neutral['600'],
    },
}));

const DescriptionContentCard: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { mappedOverviewQueries } = useContext(DashboardsContext);
    const { redirectToDashboard } = useRoute();

    const getQueryAnswer = useCallback((title: string) => (
        mappedOverviewQueries.get(`${dashboard.id}:${title}`)
    ), [dashboard, mappedOverviewQueries]);

    const description = useMemo(() => getQueryAnswer('Description') || '', [getQueryAnswer]);

    return (<>
        <Stack direction="column" className={classes.card} spacing={1} justifyContent="stretch">
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" width="100%">
                <Sentence width={24} height={24} style={{ flexShrink: 0, stroke: 'rgb(163, 167, 172)' }} />
                <Typography className={classes.title}>
                    {`Description for `}
                    <Typography component="span" className={classes.label}
                        onClick={() => redirectToDashboard(dashboard.id)}
                        sx={{ cursor: 'pointer' }}
                        display="inline">
                        {dashboard.title}
                    </Typography>
                </Typography>
            </Stack>
            <Stack className={classes.container} alignItems="center">
                <Box className={classes.noteBlock}>
                    <Typography className={classes.noteText}>
                        {description || 'No description'}
                    </Typography>
                </Box>
            </Stack>
        </Stack>
    </>);
};

export default memo(DescriptionContentCard);
